/**
 * F-idle-logout — desconexão automática por inatividade (60 min).
 *
 * Por quê: o cookie de sessão dura 7 dias (absoluto, sem renovação). Num ERP
 * financeiro acessado por celular/tablet, isso é frouxo se o aparelho for
 * perdido. Este guarda-chuva no cliente encerra a sessão após 60 min sem
 * interação, com um aviso de contagem regressiva 2 min antes.
 *
 * Como funciona:
 *  - A última atividade é gravada em localStorage (`edsp.lastActivity`), então
 *    várias abas compartilham o mesmo relógio (mexer numa zera todas).
 *  - Um tick de 1s compara `agora - lastActivity`:
 *      ≥ 58 min → mostra o aviso com contagem regressiva;
 *      ≥ 60 min → logout (limpa cookie no backend) + redireciona para /login.
 *  - Qualquer atividade (mouse/teclado/toque/scroll) some com o aviso e
 *    reinicia a contagem.
 *
 * Montado dentro do <ProtectedRoute>, logo só roda quando há sessão.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../i18n/I18nProvider.js";
import { trpc } from "../lib/trpc.js";

const IDLE_LIMIT_MS = 60 * 60 * 1000; // 60 min até o logout
const WARN_MS = 2 * 60 * 1000; // aviso 2 min antes
const TICK_MS = 1000;
const WRITE_THROTTLE_MS = 5000; // não martela o localStorage a cada mousemove
const STORAGE_KEY = "edsp.lastActivity";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
] as const;

export function IdleTimeout() {
  const t = useT();
  const logout = trpc.auth.logout.useMutation();

  // null = sem aviso; número = ms restantes até o logout (aviso visível).
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const lastWriteRef = useRef(0);
  const warningShownRef = useRef(false);
  const loggingOutRef = useRef(false);

  const readLast = useCallback((): number => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) ? n : Date.now();
    } catch {
      return Date.now();
    }
  }, []);

  const writeLast = useCallback((ts: number) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(ts));
    } catch {
      /* modo privado / quota — ignora */
    }
  }, []);

  const doLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    logout.mutate(undefined, {
      onSettled: () => {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
        // Reload total → limpa todo o estado em memória (react-query etc.).
        window.location.assign("/login");
      },
    });
  }, [logout]);

  // Marca atividade: zera o relógio (gravação throttled) e some com o aviso.
  const markActivity = useCallback(() => {
    const t0 = Date.now();
    if (warningShownRef.current || t0 - lastWriteRef.current > WRITE_THROTTLE_MS) {
      lastWriteRef.current = t0;
      writeLast(t0);
    }
    if (warningShownRef.current) {
      warningShownRef.current = false;
      setRemainingMs(null);
    }
  }, [writeLast]);

  // Inicializa o relógio na montagem (se ainda não houver).
  useEffect(() => {
    if (!window.localStorage.getItem(STORAGE_KEY)) writeLast(Date.now());
  }, [writeLast]);

  // Tick de verificação.
  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - readLast();
      if (elapsed >= IDLE_LIMIT_MS) {
        doLogout();
        return;
      }
      if (elapsed >= IDLE_LIMIT_MS - WARN_MS) {
        warningShownRef.current = true;
        setRemainingMs(IDLE_LIMIT_MS - elapsed);
      } else if (warningShownRef.current) {
        warningShownRef.current = false;
        setRemainingMs(null);
      }
    };
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [readLast, doLogout]);

  // Listeners de atividade.
  useEffect(() => {
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
  }, [markActivity]);

  if (remainingMs === null) return null;

  const secs = Math.max(0, Math.ceil(remainingMs / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  const countdown = `${mm}:${String(ss).padStart(2, "0")}`;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#000000aa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--navy-soft)",
          border: "1px solid var(--navy-elevated)",
          borderRadius: "var(--radius-lg, 16px)",
          padding: 28,
          textAlign: "center",
          boxShadow: "0 24px 60px #00000080",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 18,
            color: "var(--text-primary)",
            margin: "0 0 10px",
          }}
        >
          {t("shell.idleTitle")}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 13.5,
            color: "var(--text-secondary)",
            lineHeight: 1.45,
            margin: "0 0 16px",
          }}
        >
          {t("shell.idleBody")}
        </p>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 38,
            letterSpacing: "0.02em",
            color: "var(--lime)",
            fontVariantNumeric: "tabular-nums",
            margin: "0 0 22px",
          }}
        >
          {countdown}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={markActivity}
            style={{
              height: 48,
              border: "none",
              borderRadius: "var(--radius-pill, 9999px)",
              background: "var(--lime)",
              color: "var(--text-on-lime)",
              fontFamily: "var(--font-body)",
              fontWeight: 800,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {t("shell.idleStay")}
          </button>
          <button
            type="button"
            onClick={doLogout}
            style={{
              height: 42,
              border: "none",
              background: "transparent",
              color: "var(--text-muted)",
              fontFamily: "var(--font-body)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {t("shell.idleLogoutNow")}
          </button>
        </div>
      </div>
    </div>
  );
}
